/*
  ml-cart-glass — behaviour for the glass cart page and drawer (snippets/ml-cart-v2.liquid).
  Cart changes still go through assets/ml-cart-v2.js (window.MlCartV2): Shopify first, then a
  Section Rendering re-render, so every figure on screen is the cart's own.

  This file adds:
  - the plan <select>, the annual-upgrade checkbox and the disclosure toggles;
  - the price flip when the cart turns member-priced, and the entrance on load / drawer open;
  - the 50/50 layout test's variant on the checkout form (attributes[ml_cart_variant]);
  - the mobile checkout bar on the cart page;
  - the 30-day free trial exit offer (board P1·4, P1·5 and B1·2), shown only when the section
    rendered it (data-exit30 is set: offer on, the 30-day product resolves, a non-member with a
    membership line and products, no trial30 line yet, and a signed-in customer with no past
    membership order). Triggers: "without membership" (tile, switch, link, or removing the
    membership line), closing the drawer, and leaving the page with the pointer (desktop,
    after 8 s). At most once per session; "No" hides it for 30 days on this browser, "Yes"
    for good. Nothing changes without a click.
    "Yes" replaces the membership line with the 30-day trial at qty 1 (MlCartV2.pickRoute, which
    adds first and then removes the old line, so a failed add leaves the cart as it was) and
    then either returns to the cart or goes to checkout, per the section setting.
  - analytics through PostHog when it is on the page.
*/
(function () {
  'use strict';
  if (window.MlCartGlass) return;

  var ROOT = '[data-ml-cart-root]';
  var SHOWN = 'ml_cart_exit_shown';
  var DECLINED = 'ml_exit30_declined';
  var TAKEN = 'ml_exit30_taken';
  var DECLINE_DAYS = 30;
  var DWELL_MS = 8000;
  var loadedAt = Date.now();
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var openExit = null; // { box, root, trigger, lastFocus, surface }

  function roots() { return Array.prototype.slice.call(document.querySelectorAll(ROOT)); }
  function pageRoot() { return document.querySelector(ROOT + '[data-surface="page"]'); }
  function drawerEl() { return document.querySelector('[data-ml-cart-drawer]'); }
  function drawerRoot() { var d = drawerEl(); return d ? d.querySelector(ROOT) : null; }
  function drawerOpen() { var d = drawerEl(); return !!(d && !d.hidden && d.classList.contains('is-open')); }
  function cart() { return window.MlCartV2; }

  function ss(get, key, val) {
    try { if (get) return window.sessionStorage.getItem(key); window.sessionStorage.setItem(key, val); } catch (e) {}
    return null;
  }
  function ls(get, key, val) {
    try { if (get) return window.localStorage.getItem(key); window.localStorage.setItem(key, val); } catch (e) {}
    return null;
  }

  // ---------- analytics ----------
  function props(root, extra) {
    var p = {};
    if (root) {
      p.surface = root.getAttribute('data-surface');
      p.style = root.getAttribute('data-ml-cart-style');
      p.state = root.getAttribute('data-state');
      if (p.surface === 'page') p.layout = p.variant = root.getAttribute('data-layout');
      else p.structure = p.variant = root.getAttribute('data-structure');
    }
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) p[k] = extra[k];
    return p;
  }
  function track(name, root, extra) {
    try { if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(name, props(root, extra)); } catch (e) {}
  }

  // ---------- presentation ----------
  function syncVariant(root) {
    if (root.getAttribute('data-surface') !== 'page') return;
    var input = root.querySelector('[data-mlcg-variant-attr]');
    var layout = root.getAttribute('data-layout');
    if (input && layout) input.value = layout;
  }

  function pulse(root, cls, ms) {
    root.classList.remove(cls);
    void root.offsetWidth;
    root.classList.add(cls);
    window.setTimeout(function () { root.classList.remove(cls); }, ms);
  }

  var bar = { io: null };
  function watchBar(root) {
    if (bar.io) { bar.io.disconnect(); bar.io = null; }
    if (!root || root.getAttribute('data-surface') !== 'page' || !('IntersectionObserver' in window)) return;
    var b = root.querySelector('[data-mlcg-bar]');
    var target = root.querySelector('.mlcg-sum .mlcg-checkout-form');
    if (!b || !target) return;
    bar.io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      // Shown while the summary's checkout button is off screen below.
      b.classList.toggle('is-shown', !e.isIntersecting && e.boundingClientRect.top > 0);
    });
    bar.io.observe(target);
  }

  function lastState(root) { return root.getAttribute('data-glass-mv'); }

  function afterRender() {
    roots().forEach(function (root) {
      syncVariant(root);
      var before = lastState(root);
      var now = root.getAttribute('data-member-view');
      if (before === 'false' && now === 'true') pulse(root, 'is-flip', 2200);
      root.setAttribute('data-glass-mv', now || '');
    });
    watchBar(pageRoot());
  }

  // ---------- toggles, select, upgrade ----------
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var tog = t.closest('[data-mlcg-toggle]');
    if (tog && tog.closest(ROOT)) {
      e.preventDefault();
      var open = tog.getAttribute('aria-expanded') !== 'true';
      tog.setAttribute('aria-expanded', open ? 'true' : 'false');
      var id = tog.getAttribute('aria-controls');
      var panel = id && document.getElementById(id);
      if (panel) panel.hidden = !open;
      // The rotation follows aria-expanded; the server's static is-open only covers first paint.
      var chev = tog.querySelector('.mlcg-chev.is-open');
      if (chev) chev.classList.remove('is-open');
      var label = tog.getAttribute('data-mlcg-toggle-label');
      var text = tog.querySelector('[data-mlcg-toggle-text]');
      if (label && text) {
        if (!text.hasAttribute('data-default')) text.setAttribute('data-default', text.textContent);
        text.textContent = open ? label : text.getAttribute('data-default');
      }
      return;
    }
    var plan = t.closest('[data-mlcg-plan], .mlcg-seg__o, .mlcg-radio');
    if (plan && plan.hasAttribute('data-ml-route-pick')) {
      track('ml_cart_plan_select', plan.closest(ROOT), { plan: plan.getAttribute('data-ml-route-pick') });
    }
    var q = t.closest('[data-ml-cart-qty]');
    if (q && q.closest(ROOT)) {
      track(q.getAttribute('data-ml-cart-qty') === '0' ? 'ml_cart_remove' : 'ml_cart_qty_change', q.closest(ROOT), { qty: parseInt(q.getAttribute('data-ml-cart-qty'), 10) || 0 });
    }
  });

  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.matches) return;
    var root = el.closest && el.closest(ROOT);
    if (!root || !cart()) return;
    if (el.matches('[data-mlcg-plan-select]')) {
      track('ml_cart_plan_select', root, { plan: el.value });
      cart().pickRoute(root, el.value);
    } else if (el.matches('[data-mlcg-upgrade]')) {
      track('ml_cart_upgrade_tap', root, { on: el.checked });
      cart().pickRoute(root, el.checked ? 'annual' : 'none');
    }
  });

  document.addEventListener('toggle', function (e) {
    var d = e.target;
    if (d && d.matches && d.matches('details.mlcg-guar') && d.open) track('ml_cart_guarantee_open', d.closest(ROOT));
  }, true);

  // ---------- 30-day exit offer ----------
  function offerAllowed(root) {
    if (!root || !root.getAttribute('data-exit30')) return false;
    if (!root.querySelector('[data-mlcg-exit]')) return false;
    if (root.getAttribute('aria-busy') === 'true') return false;
    if (openExit) return false;
    if (ss(true, SHOWN)) return false;
    if (ls(true, TAKEN)) return false;
    var declined = parseInt(ls(true, DECLINED), 10);
    if (declined && Date.now() - declined < DECLINE_DAYS * 864e5) return false;
    return true;
  }

  function focusables(box) {
    return Array.prototype.slice.call(box.querySelectorAll('button:not([disabled]), a[href]'))
      .filter(function (el) { return el.getClientRects().length; });
  }

  function showOffer(root, trigger, onNo) {
    if (!offerAllowed(root)) return false;
    var src = root.querySelector('[data-mlcg-exit]');
    var t30 = root.querySelector('input[data-ml-route][value="trial30"]');
    if (!src || !t30) return false;
    ss(false, SHOWN, '1');
    // Moved to <body>: the drawer panel's backdrop-filter would otherwise contain a fixed modal.
    // The next re-render brings a fresh copy back into the root if the offer still applies.
    var box = src;
    box.setAttribute('data-ml-cart-style', root.getAttribute('data-ml-cart-style') || 'dark');
    box.classList.add('mlcg');
    box.hidden = false;
    var no = box.querySelector('.mlcg-exit__no');
    if (no && trigger === 'without') no.textContent = 'No thanks, continue without membership';
    document.body.appendChild(box);
    openExit = {
      box: box, root: root, trigger: trigger, onNo: onNo, lastFocus: document.activeElement,
      surface: root.getAttribute('data-surface'), variant: t30.getAttribute('data-variant')
    };
    document.documentElement.classList.add('ml-cart-drawer-lock');
    var yes = box.querySelector('[data-mlcg-exit-yes]');
    if (yes) yes.focus({ preventScroll: true });
    track('ml_exit30_shown', root, { trigger: trigger });
    return true;
  }

  function closeOffer(keepLock) {
    if (!openExit) return;
    var o = openExit;
    openExit = null;
    if (o.box.parentNode) o.box.parentNode.removeChild(o.box);
    if (!keepLock && !drawerOpen()) document.documentElement.classList.remove('ml-cart-drawer-lock');
    if (o.lastFocus && document.contains(o.lastFocus) && o.lastFocus.focus) o.lastFocus.focus({ preventScroll: true });
    return o;
  }

  function sayNo() {
    var o = openExit;
    if (!o) return;
    ls(false, DECLINED, String(Date.now()));
    track('ml_exit30_no', o.root, { trigger: o.trigger });
    closeOffer();
    if (typeof o.onNo === 'function') o.onNo();
  }

  function hasTrial30(variant) {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        return (c.items || []).some(function (i) { return String(i.variant_id) === String(variant) && i.quantity === 1; });
      });
  }

  function sayYes() {
    var o = openExit;
    if (!o || !cart()) return;
    if (o.box.getAttribute('aria-busy') === 'true') return;
    o.box.setAttribute('aria-busy', 'true');
    var err = o.box.querySelector('[data-mlcg-exit-err]');
    if (err) err.hidden = true;
    var after = o.root.getAttribute('data-exit30') === 'checkout' ? 'checkout' : 'cart';
    track('ml_exit30_yes', o.root, { trigger: o.trigger, after: after });
    var surface = o.surface;
    cart().pickRoute(o.root, 'trial30')
      .then(function () {
        var qty = window.MlMembershipQty && window.MlMembershipQty.normalize ? window.MlMembershipQty.normalize().catch(function () {}) : null;
        return Promise.resolve(qty).then(function () { return hasTrial30(o.variant); });
      })
      .then(function (ok) {
        if (!ok) throw new Error('trial30-missing');
        ls(false, TAKEN, '1');
        if (after === 'checkout') {
          // Through the checkout form, so the checkout guard and the cart attributes run as usual.
          var fresh = document.querySelector(ROOT + '[data-surface="' + surface + '"]') || o.root;
          var form = fresh.querySelector('.mlcg-checkout-form');
          var btn = form && form.querySelector('button[name="checkout"]');
          if (form && btn && form.requestSubmit) { form.requestSubmit(btn); return; }
          window.location.href = '/checkout';
          return;
        }
        closeOffer(surface === 'drawer');
        var root = document.querySelector(ROOT + '[data-surface="' + surface + '"]');
        if (root) {
          pulse(root, 'is-flip', 2200);
          var h = surface === 'drawer' ? root.querySelector('#MlCartDrawerTitle') : root.querySelector('.mlcg-free__head, .mlcg-page__h');
          if (h && h.focus) h.focus({ preventScroll: false });
        }
      })
      .catch(function () {
        if (!openExit) return;
        openExit.box.removeAttribute('aria-busy');
        if (err) err.hidden = false;
        track('ml_exit30_error', o.root, {});
      });
  }

  // Clicks inside the offer.
  document.addEventListener('click', function (e) {
    if (!openExit || !e.target || !e.target.closest) return;
    if (!openExit.box.contains(e.target)) return;
    if (e.target.closest('[data-mlcg-exit-yes]')) { e.preventDefault(); sayYes(); return; }
    if (e.target.closest('[data-mlcg-exit-no]')) { e.preventDefault(); sayNo(); }
  });

  // Keyboard inside the offer (capture: runs before the drawer's own Esc and focus trap).
  window.addEventListener('keydown', function (e) {
    if (!openExit) return;
    if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); sayNo(); return; }
    if (e.key !== 'Tab') return;
    e.stopImmediatePropagation();
    var items = focusables(openExit.box);
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1], a = document.activeElement;
    if (e.shiftKey && (a === first || !openExit.box.contains(a))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (a === last || !openExit.box.contains(a))) { e.preventDefault(); first.focus(); }
  }, true);

  // Trigger: "without membership" and removing the membership line (capture: before ml-cart-v2).
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest || openExit) return;
    var root = t.closest(ROOT);
    if (!root || !root.getAttribute('data-exit30')) return;
    var el = t.closest('[data-ml-route-pick="none"]:not([data-mlcg-no-offer])');
    if (!el) {
      var q = t.closest('[data-ml-cart-qty="0"]');
      var keys = (root.getAttribute('data-mem-keys') || '').split(',');
      if (q && keys.indexOf(q.getAttribute('data-key')) !== -1) el = q;
    }
    if (!el) return;
    var shown = showOffer(root, 'without', function () {
      if (el.hasAttribute('data-ml-cart-qty')) cart() && cart().changeQty(el.getAttribute('data-key'), 0);
      else cart() && cart().pickRoute(root, 'none');
    });
    if (shown) { e.preventDefault(); e.stopPropagation(); }
  }, true);

  // Trigger: closing the drawer (x, backdrop, Esc).
  function interceptClose(e) {
    var root = drawerRoot();
    if (!drawerOpen() || !root) return false;
    var shown = showOffer(root, 'drawer_close', function () {
      if (window.MonthleesCartDrawer && window.MonthleesCartDrawer.close) window.MonthleesCartDrawer.close();
    });
    if (shown) { e.preventDefault(); e.stopImmediatePropagation(); }
    return shown;
  }
  document.addEventListener('click', function (e) {
    if (openExit || !e.target || !e.target.closest) return;
    var c = e.target.closest('[data-ml-cart-close]');
    if (c && c.closest('[data-ml-cart-drawer]')) interceptClose(e);
  }, true);
  window.addEventListener('keydown', function (e) {
    if (openExit || e.key !== 'Escape') return;
    interceptClose(e);
  }, true);

  // Trigger: pointer leaves through the top of the window (desktop only).
  var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  var drawerOpenedAt = 0;
  document.addEventListener('mouseout', function (e) {
    if (!fine || openExit || e.relatedTarget || e.clientY > 0) return;
    var root = drawerOpen() ? drawerRoot() : pageRoot();
    if (!root) return;
    var since = drawerOpen() ? drawerOpenedAt : loadedAt;
    if (Date.now() - since < DWELL_MS) return;
    showOffer(root, 'leave', null);
  });

  // ---------- drawer entrance ----------
  function watchDrawer() {
    var d = drawerEl();
    if (!d || !('MutationObserver' in window)) return;
    var was = d.classList.contains('is-open');
    new MutationObserver(function () {
      var now = d.classList.contains('is-open');
      if (now && !was) {
        drawerOpenedAt = Date.now();
        var r = drawerRoot();
        if (r && !reduced) { pulse(r, 'is-enter', 1600); pulse(r, 'is-guar', 3400); }
        if (r) track('ml_cart_view', r);
      }
      was = now;
    }).observe(d, { attributes: true, attributeFilter: ['class'] });
  }

  document.addEventListener('ml-cart-v2:rendered', function () {
    afterRender();
    // A drawer that re-rendered while opening keeps its entrance.
    var r = drawerRoot();
    if (r && drawerOpen() && Date.now() - drawerOpenedAt < 1200 && !reduced) pulse(r, 'is-enter', 1600);
  });

  function init() {
    var d = drawerEl();
    if (d) {
      var dr = drawerRoot();
      if (dr) d.setAttribute('data-ml-cart-style', dr.getAttribute('data-ml-cart-style') || 'dark');
    }
    roots().forEach(function (root) {
      syncVariant(root);
      root.setAttribute('data-glass-mv', root.getAttribute('data-member-view') || '');
    });
    var page = pageRoot();
    if (page) {
      if (!reduced) {
        pulse(page, 'is-enter', 1600);
        pulse(page, 'is-guar', 3400);
        if (page.getAttribute('data-member-view') === 'true') pulse(page, 'is-flip', 2200);
      }
      track('ml_cart_view', page);
      watchBar(page);
    }
    watchDrawer();
  }

  window.MlCartGlass = { showOffer: showOffer, closeOffer: closeOffer };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
